import { AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  Box,
  ErrorBoundary,
  Stack,
  SxProps,
  Text,
  useTheme,
} from '@ltvco/refresh-lib/theme';
import {
  commonTypes,
  SerializedPerson,
  serializedTypes,
  useSession,
} from '@ltvco/refresh-lib/v1';
import {
  useSriracha,
  RelatedReports,
  RelatedReport,
} from '@ltvco/refresh-lib/v2';
import { useContext, useState } from 'react';

import {
  ReportChatCard,
  MessageContainerDivider,
} from 'components/ChatBV/components';
import { LOADING_STATUS } from 'components/ChatBV/constants';
import { ChatContext } from 'components/ChatBV/context';

export interface RelatedReportsSectionProps {
  alternateTitle?: string;
  hideSectionDescription?: boolean;
  personName: string;
  reportType: string;
  potentialOwners: SerializedPerson[] | undefined;
  relatives: serializedTypes.Connection[] | undefined;
  addresses: commonTypes.Addresses;
  phones: commonTypes.Phones;
  emails: commonTypes.Emails;
  usernames: commonTypes.Usernames;
  hidePersonSection?: boolean;
  iconStyles?: SxProps;
  handleRelatedReportClick: (report: RelatedReport) => void;
}

const Section = ({
  personName,
  reportType,
  potentialOwners,
  relatives,
  addresses,
  phones,
  emails,
  usernames,
  hidePersonSection,
  iconStyles,
  handleRelatedReportClick,
}: RelatedReportsSectionProps) => {
  const [hasGeneratedNotifications, setHasGeneratedNotifications] =
    useState(false);
  const {
    session: { account },
  } = useSession();
  const theme = useTheme();
  const userId = account?.account.user_info?.user_code;
  const { sendNotification, getSuggestedReportNotificationSources } =
    useSriracha();
  const { data } = getSuggestedReportNotificationSources;
  const { loadingStatus } = useContext(ChatContext);
  const isLoading = loadingStatus?.message !== LOADING_STATUS.IDLE.message;

  const relatedReports = new RelatedReports(
    reportType,
    personName,
    potentialOwners,
    relatives,
    addresses,
    phones,
    emails,
    usernames
  );

  const generateSuggestedReportNotification = (notificationData: unknown) => {
    sendNotification.mutate(notificationData);
  };

  const relatedReportList = relatedReports.getRelatedReports();

  // Helper function to get the appropriate title for a report
  const getReportTitle = (report: any, personName: string): string => {
    if (report.dataPoint === 'N/A' || report.dataPoint === '') {
      switch (report.reportType) {
        case 'person':
          return report.data?.identity?.names?.[0]?.full || personName;
        case 'phone':
          return report.data?.number || '';
        case 'username':
          return report.data?.username || '';
        default:
          return report.dataPoint || '';
      }
    }
    return report.dataPoint || '';
  };

  if (userId && data && !hasGeneratedNotifications) {
    const suggestedReportNotificationSources = data?.suggested_report_sources;
    if (
      relatedReports.shouldGenerateSuggestedReportNotifications(
        suggestedReportNotificationSources
      )
    ) {
      setHasGeneratedNotifications(true);
      relatedReports.generateNotificationsForSuggestedReports(
        generateSuggestedReportNotification,
        relatedReportList,
        userId
      );
    }
  }

  // Don't render anything if there are no related reports
  if (!relatedReportList || relatedReportList.length === 0) {
    return <MessageContainerDivider />;
  }

  return (
    <Stack spacing={1.5}>
      <Text variant="h5" marginTop="16px !important">
        Related reports
      </Text>
      {relatedReportList.map(
        (report, index) =>
          (report.reportType !== 'person' || !hidePersonSection) && (
            <>
              <ReportChatCard
                title={getReportTitle(report, personName)}
                reportType={report.reportType}
                key={`${report.reportType}-${index}`}
                description={
                  <Text color={'text.secondary'}>
                    {report.relatedReportType}
                  </Text>
                }
                clickAction={() => handleRelatedReportClick(report)}
                iconStyles={iconStyles}
                disabled={isLoading}
              />
              <Box
                sx={{
                  marginBottom:
                    index === relatedReportList.length - 1
                      ? theme.spacing(3)
                      : 0,
                }}
              >
                <MessageContainerDivider />
              </Box>
            </>
          )
      )}
    </Stack>
  );
};

export const RelatedReportsChat = ({
  alternateTitle,
  hideSectionDescription,
  personName,
  reportType,
  potentialOwners,
  relatives,
  addresses,
  phones,
  emails,
  usernames,
  hidePersonSection,
  iconStyles,
  handleRelatedReportClick,
}: RelatedReportsSectionProps) => {
  const { logError, trackEventGA4 } = useContext(AppConfig);

  return (
    <ErrorBoundary
      trackEventFunction={trackEventGA4}
      logFunction={logError as any}
      section="related-reports-section"
    >
      <Section
        personName={personName}
        reportType={reportType}
        potentialOwners={potentialOwners}
        alternateTitle={alternateTitle}
        hideSectionDescription={hideSectionDescription}
        relatives={relatives}
        addresses={addresses}
        phones={phones}
        emails={emails}
        usernames={usernames}
        hidePersonSection={hidePersonSection}
        iconStyles={iconStyles}
        handleRelatedReportClick={handleRelatedReportClick}
      />
    </ErrorBoundary>
  );
};
